import { createFileRoute } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Suspense, useMemo, useState } from "react";
import { aiaAwardsQueryOptions, type AiaAward } from "@/lib/aia-awards.queries";
import { Button } from "@/components/ui/button";
import { PageHero } from "@/components/site/PageHero";
import { StoredLink } from "@/components/site/StoredLink";
import { ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/awards")({
  head: () => ({
    meta: [
      { title: "AIA Awards — Medical Insight" },
      {
        name: "description",
        content:
          "AIA Industry Awards and Leadership Awards recognizing outstanding technology, marketing, and leadership in the aesthetic industry.",
      },
    ],
  }),
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(aiaAwardsQueryOptions());
  },
  component: AwardsPage,
});

function AwardsPage() {
  return (
    <Suspense fallback={<div className="mx-auto max-w-7xl px-4 py-16">Loading…</div>}>
      <AwardsContent />
    </Suspense>
  );
}

function AwardsContent() {
  const { data: awards } = useSuspenseQuery(aiaAwardsQueryOptions());
  const industry = useMemo(() => awards.filter((a) => a.category === "industry"), [awards]);
  const leadership = useMemo(() => awards.filter((a) => a.category === "leadership"), [awards]);

  return (
    <div className="bg-background">
      <PageHero
        page="awards"
        /* Bright celebratory header per customer request 2026-09-01 — the
           per-direction bundled defaults stay as fallback if this 404s. */
        bgSrc="/media/cms-images/aia/awards-header-20260901.jpg"
        scrim="light"
        kicker="Aesthetic Industry Association"
        title="AIA Awards"
        subtitle="Recognizing the technologies, campaigns, and leaders moving the aesthetic industry forward."
      />

      <div className="bg-secondary/30">
        <div className="mx-auto max-w-7xl px-4 pb-16">
          <AwardSection
            title="AIA Industry Awards"
            subtitle="Awarded by the AIA board for outstanding products, technology, and marketing."
            awards={industry}
            imageShape="logo"
          />
          <AwardSection
            title="AIA Leadership Awards"
            subtitle="Honoring individual leadership and lasting contributions to the industry."
            awards={leadership}
            imageShape="portrait"
          />
        </div>
      </div>
    </div>
  );
}

function AwardSection({
  title,
  subtitle,
  awards,
  imageShape,
}: {
  title: string;
  subtitle: string;
  awards: AiaAward[];
  imageShape: "logo" | "portrait";
}) {
  if (awards.length === 0) return null;
  return (
    <section className="mt-14 first-of-type:mt-10">
      <div className="border-b pb-3">
        <h2 className="text-xl font-bold tracking-tight sm:text-2xl">{title}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
      </div>
      {/* Industry Awards run one card per row with a 250px logo box; Leadership Awards stay
          two across with a 150px circle (customer decisions 2026-09-01 and 2026-09-15). */}
      <div className={cn("mt-6 grid gap-5", imageShape === "portrait" && "sm:grid-cols-2")}>
        {awards.map((a) => (
          <AwardCard key={a.id} award={a} imageShape={imageShape} />
        ))}
      </div>
    </section>
  );
}

function AwardCard({ award, imageShape }: { award: AiaAward; imageShape: "logo" | "portrait" }) {
  const [expanded, setExpanded] = useState(false);
  const portrait = imageShape === "portrait";
  return (
    <div className="flex flex-col rounded-lg border bg-card p-6 transition-shadow hover:shadow-sm">
      {/* Graphic on the left, text on the right; stacked (graphic first) below `sm`. The box
          (250px for logos, 150px for portraits) is always rendered so titles line up even when
          an award has no image. */}
      <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
        <div
          className={cn(
            "flex shrink-0 items-center justify-center",
            portrait ? "size-[150px] rounded-full bg-muted" : "size-[250px] rounded-md bg-white",
          )}
        >
          {award.image_path && (
            <img
              src={award.image_path}
              alt={award.name}
              className={
                portrait
                  ? // The leadership photos are already circle-masked, near-square PNGs, so a
                    // square box with object-cover shows the whole disc.
                    "size-full rounded-full object-cover"
                  : "size-full rounded-md object-contain"
              }
            />
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col">
          <h3 className="text-lg font-semibold leading-snug">{award.name}</h3>
          {award.description && (
            <p
              className={`mt-3 flex-1 text-sm leading-relaxed text-muted-foreground text-left ${
                expanded ? "" : "line-clamp-4"
              }`}
            >
              {award.description}
            </p>
          )}
          <div className="mt-3 flex items-center gap-4">
            {award.description && award.description.length > 220 && (
              <Button
                variant="link"
                size="sm"
                className="h-auto p-0 text-xs"
                onClick={() => setExpanded((v) => !v)}
              >
                {expanded ? "Show less" : "Show more"}
              </Button>
            )}
            {award.article_url && (
              <StoredLink
                url={award.article_url}
                className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              >
                {(external) => <>Full story {external && <ExternalLink className="h-3 w-3" />}</>}
              </StoredLink>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
